import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IProductDetails } from "../services/productApi/interface";

const defaultProduct: IProductDetails = {
  name: "New Product",
  description: "A description for the new product",
  price: 0,
  discount: 0,
  discount_type: "",
  stock: 0,
  category: [],
  size_options: [],
  gender_options: [],
  color_options: [],
  position_in_store: "",
  store_id: "",
  model_file: "",
  image_file: "",
  quantity: 0,
};

const initialState: IProductDetails[] = [];

interface ProductPayload<T> {
  index: number;
  value: T;
}

const createProductSlice = createSlice({
  name: "CreateProduct",
  initialState,
  reducers: {
    // Reducers to add and remove products from the array
    addProduct: (state, action: PayloadAction<IProductDetails | undefined>) => {
      // If a product is provided, use it, otherwise use the default
      state.push(action.payload || defaultProduct);
    },
    removeProduct: (state, action: PayloadAction<number>) => {
      state.splice(action.payload, 1);
    },

    // Modified reducers to accept an index for updating a specific product
    setName: (state, action: PayloadAction<ProductPayload<string>>) => {
      if (state[action.payload.index]) {
        state[action.payload.index].name = action.payload.value;
      }
    },
    setDescription: (state, action: PayloadAction<ProductPayload<string>>) => {
      if (state[action.payload.index]) {
        state[action.payload.index].description = action.payload.value;
      }
    },
    setPrice: (state, action: PayloadAction<ProductPayload<number>>) => {
      if (state[action.payload.index]) {
        state[action.payload.index].price = action.payload.value;
      }
    },
    setDiscount: (state, action: PayloadAction<ProductPayload<number>>) => {
      if (state[action.payload.index]) {
        state[action.payload.index].discount = action.payload.value;
      }
    },
    setDiscountType: (state, action: PayloadAction<ProductPayload<string>>) => {
      if (state[action.payload.index]) {
        state[action.payload.index].discount_type = action.payload.value;
      }
    },
    setStock: (state, action: PayloadAction<ProductPayload<number>>) => {
      if (state[action.payload.index]) {
        state[action.payload.index].stock = action.payload.value;
      }
    },
    setCategory: (state, action: PayloadAction<ProductPayload<string[]>>) => {
      if (state[action.payload.index]) {
        state[action.payload.index].category = action.payload.value;
      }
    },
    setSizeOptions: (state, action: PayloadAction<ProductPayload<string[]>>) => {
      if (state[action.payload.index]) {
        state[action.payload.index].size_options = action.payload.value;
      }
    },
    setGenderOptions: (state, action: PayloadAction<ProductPayload<string[]>>) => {
      if (state[action.payload.index]) {
        state[action.payload.index].gender_options = action.payload.value;
      }
    },
    setColorOptions: (state, action: PayloadAction<ProductPayload<string[]>>) => {
      if (state[action.payload.index]) {
        state[action.payload.index].color_options = action.payload.value;
      }
    },
    setPositionInStore: (state, action: PayloadAction<ProductPayload<string>>) => {
      if (state[action.payload.index]) {
        state[action.payload.index].position_in_store = action.payload.value;
      }
    },
    setStoreId: (state, action: PayloadAction<ProductPayload<string>>) => {
      if (state[action.payload.index]) {
        state[action.payload.index].store_id = action.payload.value;
      }
    },
    setModelFile: (state, action: PayloadAction<ProductPayload<string>>) => {
      if (state[action.payload.index]) {
        state[action.payload.index].model_file = action.payload.value;
      }
    },
    setImageFile: (state, action: PayloadAction<ProductPayload<string>>) => {
      if (state[action.payload.index]) {
        state[action.payload.index].image_file = action.payload.value;
      }
    },

    resetStoreState: () => {
      return []; 
    },
  },
});

export const {
  addProduct,
  removeProduct,
  setName,
  setDescription,
  setPrice,
  setDiscount,
  setDiscountType,
  setStock,
  setCategory,
  setSizeOptions,
  setGenderOptions,
  setColorOptions,
  setPositionInStore,
  setStoreId,
  setModelFile,
  setImageFile,
  resetStoreState,
} = createProductSlice.actions;

export default createProductSlice.reducer;